import imageCompression from "browser-image-compression";

/** Vercel ~4.5 MB per begaran. Vi siktar langt under sa multipart + marginal inte sprangs gransen. */
const SAFE_MAX_BYTES = 4 * 1024 * 1024;

/** Under detta skickas filen vidare orörd (redan tillräckligt liten för webb). */
const SKIP_BELOW_BYTES = 600 * 1024;

/**
 * Kompakt uppladdning: kraftigt nedskalade bilder (bra nog för offert / köksfoton).
 * Större filer -> JPEG med lägre kvalitet och maxbredd tills vi är under gränsen.
 *
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function compressImageForUpload(file) {
  if (!file || !file.size) return file;
  if (!file.type?.startsWith("image/")) return file;

  if (file.size <= SKIP_BELOW_BYTES) {
    return file;
  }

  const baseName = (file.name || "photo").replace(/\.[^.]+$/, "");
  /** PNG med transparens: forsok PNG forst, annars JPEG (ofta 5-10x mindre). */
  const preferPng = /png/i.test(file.type) && file.size < 4 * 1024 * 1024;

  const attempts = [
    // Steg 1-2: normal webb-kvalitet
    { maxSizeMB: 0.95, maxWidthOrHeight: 1920, initialQuality: 0.72, png: preferPng },
    { maxSizeMB: 0.65, maxWidthOrHeight: 1600, initialQuality: 0.62, png: false },
    // Steg 3-5: kraftigt komprimerat men ok for bedomning
    { maxSizeMB: 0.45, maxWidthOrHeight: 1280, initialQuality: 0.55, png: false },
    { maxSizeMB: 0.32, maxWidthOrHeight: 1024, initialQuality: 0.48, png: false },
    { maxSizeMB: 0.22, maxWidthOrHeight: 900, initialQuality: 0.42, png: false },
  ];

  for (const step of attempts) {
    const asPng = step.png;
    try {
      const blob = await imageCompression(file, {
        maxSizeMB: step.maxSizeMB,
        maxWidthOrHeight: step.maxWidthOrHeight,
        initialQuality: step.initialQuality,
        useWebWorker: true,
        fileType: asPng ? "image/png" : "image/jpeg",
      });
      if (blob.size <= SAFE_MAX_BYTES) {
        const ext = asPng ? ".png" : ".jpg";
        return new File([blob], `${baseName}${ext}`, {
          type: blob.type || (asPng ? "image/png" : "image/jpeg"),
          lastModified: Date.now(),
        });
      }
    } catch {
      /* nasta steg */
    }
  }

  if (file.size > SAFE_MAX_BYTES) {
    const err = new Error(
      "Bilden gick inte att göra tillräckligt liten. Prova ta en ny bild med lägre kvalitet i kameran eller ladda upp färre bilder åt gången."
    );
    err.code = "UPLOAD_TOO_LARGE";
    throw err;
  }

  return file;
}
